import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  List,
  Loading,
  TopToolbar,
  useNotify,
} from "react-admin";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  IconButton,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { makeStyles } from "@material-ui/core/styles";
import { apiUrl } from "../../dataProvider";
import axios from "axios";

const useStyles = makeStyles({
  container: {
    width: "100%",
    padding: 0,
  },
  formControl: {
    minWidth: 200,
    marginRight: "16px",
  },
  draggableRow: {
    cursor: "grab",
    userSelect: "none",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    "&:active": {
      cursor: "grabbing",
    },
  },
  draggingRow: {
    opacity: 0.5,
    cursor: "grabbing",
  },
  dragOverRow: {
    backgroundColor: "#e3f2fd",
    borderTop: "2px solid #2196f3",
  },
  buttonContainer: {
    display: "flex",
    gap: "12px",
    marginLeft: "16px",
  },
  categoryRow: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
    cursor: "grab",
    userSelect: "none",
    "&:hover": {
      backgroundColor: "#e8e8e8",
    },
    "&:active": {
      cursor: "grabbing",
    },
  },
  subRow: {
    backgroundColor: "#fafafa",
  },
});

const SortOrderListActions = ({
  hasChanges,
  onSave,
  onDiscard,
  isSaving,
  builderPlatforms,
  selectedBuilderPlatform,
  onBuilderPlatformChange,
}) => {
  const classes = useStyles();
  return (
    <TopToolbar className={classes.container}>
      <Box
        display={"flex"}
        alignItems={"flex-end"}
        justifyContent="space-between"
        width={"100%"}
        sx={{ paddingBottom: "14px" }}
      >
        <Box>
          <FormControl className={classes.formControl} size="small">
            <InputLabel 
              id="builder-platform-select-label"
              shrink={true}
            >
              Builder Platform
            </InputLabel>
            <Select
              labelId="builder-platform-select-label"
              id="builder-platform-select"
              value={selectedBuilderPlatform}
              onChange={(e) => onBuilderPlatformChange(e.target.value)}
              label="Builder Platform"
              displayEmpty
            >
              <MenuItem value="">
                Default
              </MenuItem>
              {builderPlatforms.map((platform) => (
                <MenuItem key={platform.id} value={platform.id}>
                  {platform.store_name || platform.name || `Platform ${platform.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {hasChanges && (
          <Box className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="primary"
              onClick={onSave}
              size="small"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={onDiscard}
              size="small"
              disabled={isSaving}
            >
              Discard
            </Button>
          </Box>
        )}
      </Box>
    </TopToolbar>
  );
};

const CategoryRow = ({
  category,
  categoryIndex,
  materials,
  isExpanded,
  onToggle,
  isDragging,
  dragOverIndex,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  classes,
}) => {
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  const handleDragStart = (e) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", `category-${categoryIndex}`);
    onDragStart(categoryIndex);
  };

  const handleDragEnd = (e) => {
    e.stopPropagation();
    setIsDraggedOver(false);
    if (onDragEnd) {
      onDragEnd();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setIsDraggedOver(true);
    onDragOver(categoryIndex);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggedOver(true);
    onDragOver(categoryIndex);
  };

  const handleDragLeave = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (
      x < rect.left ||
      x > rect.right ||
      y < rect.top ||
      y > rect.bottom
    ) {
      setIsDraggedOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggedOver(false);
    const draggedData = e.dataTransfer.getData("text/plain");
    if (draggedData && draggedData.startsWith("category-")) {
      const draggedCategoryIndex = parseInt(draggedData.replace("category-", ""));
      if (draggedCategoryIndex !== categoryIndex) {
        onDrop(draggedCategoryIndex, categoryIndex);
      }
    }
  };

  const handleToggleClick = (e) => {
    // Only toggle if not dragging
    if (!isDragging) {
      onToggle();
    }
  };

  const rowClass =
    isDragging
      ? `${classes.categoryRow} ${classes.draggingRow}`
      : dragOverIndex || isDraggedOver
      ? `${classes.categoryRow} ${classes.dragOverRow}`
      : classes.categoryRow;

  return (
    <TableRow
      className={rowClass}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
    >
      <TableCell onClick={handleToggleClick} style={{ width: "70%" }}>
        <Box display="flex" alignItems="center">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleClick(); }}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          {category.option_category} 
        </Box>
      </TableCell>
      <TableCell onClick={handleToggleClick} align="right" style={{ width: "30%" }}>
        {/* {category.sort_order != null ? category.sort_order : ""} */}
      </TableCell>
    </TableRow>
  );
};

const DraggableMaterialRow = ({
  material,
  categoryIndex,
  materialIndex,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragging,
  dragOverIndex,
  draggedCategoryIndex,
}) => {
  const classes = useStyles();
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const uniqueId = `${categoryIndex}-${materialIndex}`;

  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", JSON.stringify({ categoryIndex, materialIndex }));
    onDragStart(categoryIndex, materialIndex);
  };

  const handleDragEnd = (e) => {
    e.stopPropagation();
    setIsDraggedOver(false);
    if (onDragEnd) {
      onDragEnd();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Check if dragging to a different category - prevent visual feedback
    if (draggedCategoryIndex !== null && draggedCategoryIndex !== categoryIndex) {
      e.dataTransfer.dropEffect = "none";
      setIsDraggedOver(false);
      onDragOver(categoryIndex, materialIndex);
      return;
    }
    e.dataTransfer.dropEffect = "move";
    setIsDraggedOver(true);
    onDragOver(categoryIndex, materialIndex);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Check if dragging to a different category - prevent visual feedback
    if (draggedCategoryIndex !== null && draggedCategoryIndex !== categoryIndex) {
      e.dataTransfer.dropEffect = "none";
      setIsDraggedOver(false);
      onDragOver(categoryIndex, materialIndex);
      return;
    }
    setIsDraggedOver(true);
    onDragOver(categoryIndex, materialIndex);
  };

  const handleDragLeave = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (
      x < rect.left ||
      x > rect.right ||
      y < rect.top ||
      y > rect.bottom
    ) {
      setIsDraggedOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggedOver(false);
    const draggedData = e.dataTransfer.getData("text/plain");
    if (draggedData) {
      try {
        const { categoryIndex: draggedCat, materialIndex: draggedMat } = JSON.parse(draggedData);
        if (draggedCat === categoryIndex && draggedMat !== materialIndex) {
          onDrop(draggedCat, draggedMat, categoryIndex, materialIndex);
        }
      } catch (error) {
        console.error("Error parsing drag data:", error);
      }
    }
  };

  const rowClass =
    isDragging === uniqueId
      ? `${classes.draggableRow} ${classes.draggingRow}`
      : dragOverIndex === uniqueId || isDraggedOver
      ? `${classes.draggableRow} ${classes.dragOverRow}`
      : classes.draggableRow;

  return (
    <TableRow
      className={`${rowClass} ${classes.subRow}`}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
    >
      <TableCell
        draggable={false}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        style={{ paddingLeft: "40px", width: "70%" }}
      >
        {material.material_name ?? ""}
      </TableCell>
      <TableCell
        draggable={false}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        align="center"
        style={{ width: "30%" }}
      >
        {material.sort_order != null ? material.sort_order : ""}
      </TableCell>
    </TableRow>
  );
};

const SortOrderTable = ({
  onDataChange,
  onHasChangesChange,
  onDiscardRef,
  onRefetchRef,
  builderPlatformId,
  onInvalidDrop,
}) => {
  const classes = useStyles();
  const [originalData, setOriginalData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDraggingCategory, setIsDraggingCategory] = useState(null);
  const [dragOverCategoryIndex, setDragOverCategoryIndex] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [draggedCategoryIndex, setDraggedCategoryIndex] = useState(null);
  const notificationShownRef = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${apiUrl}/ring-material-sort-order`;
      if (builderPlatformId) {
        url += `?builder_platform_id=${builderPlatformId}`;
      }
      const response = await axios.get(url);
      const responseData = response.data.data || [];
      setOriginalData(responseData);
      setTableData(responseData);
      onDataChange(responseData);
      onHasChangesChange(false);
      // Expand all categories by default (track by option_category name)
      setExpandedCategories(new Set(responseData.map((cat) => cat.option_category)));
    } catch (error) {
      setOriginalData([]);
      setTableData([]);
      onDataChange([]);
    } finally {
      setLoading(false);
    }
  }, [builderPlatformId, onDataChange, onHasChangesChange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Expose refetch method via ref
  useEffect(() => {
    if (onRefetchRef) {
      onRefetchRef.current = fetchData;
    }
  }, [fetchData, onRefetchRef]);

  const handleCategoryDragStart = useCallback((categoryIndex) => {
    setIsDraggingCategory(categoryIndex);
    setDragOverCategoryIndex(null);
  }, []);

  const handleCategoryDragOver = useCallback((categoryIndex) => {
    setDragOverCategoryIndex(categoryIndex);
  }, []);

  const handleCategoryDragEnd = useCallback(() => {
    setIsDraggingCategory(null);
    setDragOverCategoryIndex(null);
  }, []);

  const handleCategoryDrop = useCallback(
    (draggedCategoryIndex, targetCategoryIndex) => {
      setIsDraggingCategory(null);
      setDragOverCategoryIndex(null);

      if (!tableData || tableData.length === 0) return;
      if (draggedCategoryIndex === targetCategoryIndex) return;

      // Create a new array with reordered categories
      const newData = [...tableData];
      const [draggedCategory] = newData.splice(draggedCategoryIndex, 1);
      newData.splice(targetCategoryIndex, 0, draggedCategory);

      // Update category sort_order values: top category = 0, then 1, 2, 3...
      const updatedData = newData.map((category, index) => ({
        ...category,
        sort_order: index,
      }));

      // Expanded categories are tracked by option_category name, so no need to update
      setTableData(updatedData);
      onDataChange(updatedData);
      onHasChangesChange(true);
    },
    [tableData, onDataChange, onHasChangesChange]
  );

  const handleDragStart = useCallback((categoryIndex, materialIndex) => {
    setIsDragging(`${categoryIndex}-${materialIndex}`);
    setDraggedCategoryIndex(categoryIndex);
    setDragOverIndex(null);
    notificationShownRef.current = false;
  }, []);

  const handleDragOver = useCallback((categoryIndex, materialIndex) => {
    // Check if dragging to a different category
    if (draggedCategoryIndex !== null && draggedCategoryIndex !== categoryIndex) {
      if (onInvalidDrop && !notificationShownRef.current) {
        onInvalidDrop();
        notificationShownRef.current = true;
      }
      // Don't set dragOverIndex for invalid drops to prevent visual feedback
      return;
    }
    setDragOverIndex(`${categoryIndex}-${materialIndex}`);
  }, [draggedCategoryIndex, onInvalidDrop]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(null);
    setDragOverIndex(null);
    setDraggedCategoryIndex(null);
    notificationShownRef.current = false;
  }, []);

  const handleDrop = useCallback(
    (draggedCategoryIndex, draggedMaterialIndex, targetCategoryIndex, targetMaterialIndex) => {
      setIsDragging(null);
      setDragOverIndex(null);

      if (!tableData || tableData.length === 0) return;
      if (draggedCategoryIndex !== targetCategoryIndex) return; // Only allow reordering within same category

      // Create a deep copy of the data
      const newData = tableData.map((category) => ({
        ...category,
        materials: [...category.materials],
      }));

      // Get the category
      const category = newData[draggedCategoryIndex];
      if (!category || !category.materials) return;

      // Reorder materials within the category
      const materials = [...category.materials];
      const [draggedMaterial] = materials.splice(draggedMaterialIndex, 1);
      materials.splice(targetMaterialIndex, 0, draggedMaterial);

      // Update sort_order values: top material = 0, then 1, 2, 3...
      const updatedMaterials = materials.map((material, index) => ({
        ...material,
        sort_order: index,
      }));

      // Update the category with reordered materials
      newData[draggedCategoryIndex] = {
        ...category,
        materials: updatedMaterials,
      };

      setTableData(newData);
      onDataChange(newData);
      onHasChangesChange(true);
    },
    [tableData, onDataChange, onHasChangesChange]
  );

  const handleDiscard = useCallback(() => {
    setTableData(originalData);
    onDataChange(originalData);
    onHasChangesChange(false);
  }, [originalData, onDataChange, onHasChangesChange]);

  // Expose discard method via ref
  useEffect(() => {
    if (onDiscardRef) {
      onDiscardRef.current = handleDiscard;
    }
  }, [handleDiscard, onDiscardRef]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Loading />
      </Box>
    );
  }

  const toggleCategory = (categoryName) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  if (!tableData || tableData.length === 0) {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Material Name</TableCell>
            <TableCell>Sort Order</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell colSpan={2} align="center">
              No records found
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell style={{ width: "70%" }}>Material Name</TableCell>
          <TableCell style={{ width: "30%" }} align="center">Sort Order</TableCell>
        </TableRow>
      </TableHead>
      <TableBody
        onDragOver={(e) => {
          e.preventDefault();
        }}
      >
        {tableData.map((category, categoryIndex) => {
          const isExpanded = expandedCategories.has(category.option_category);
          const materials = category.materials || [];

          return (
            <React.Fragment key={categoryIndex}>
              {/* Category Header Row */}
              <CategoryRow
                category={category}
                categoryIndex={categoryIndex}
                materials={materials}
                isExpanded={isExpanded}
                onToggle={() => toggleCategory(category.option_category)}
                isDragging={isDraggingCategory === categoryIndex}
                dragOverIndex={dragOverCategoryIndex === categoryIndex}
                onDragStart={handleCategoryDragStart}
                onDragOver={handleCategoryDragOver}
                onDrop={handleCategoryDrop}
                onDragEnd={handleCategoryDragEnd}
                classes={classes}
              />
              {/* Category Materials */}
              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Table size="small" style={{ width: "100%", tableLayout: "fixed" }}>
                      <TableBody>
                        {materials.map((material, materialIndex) => {
                          return (
                            <DraggableMaterialRow
                              key={`${categoryIndex}-${materialIndex}`}
                              material={material}
                              categoryIndex={categoryIndex}
                              materialIndex={materialIndex}
                              isDragging={isDragging}
                              dragOverIndex={dragOverIndex}
                              draggedCategoryIndex={draggedCategoryIndex}
                              onDragStart={handleDragStart}
                              onDragOver={handleDragOver}
                              onDrop={handleDrop}
                              onDragEnd={handleDragEnd}
                            />
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

const SortOrderList = (props) => {
  const { permissions } = props;
  const notify = useNotify();
  const [currentData, setCurrentData] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const discardRef = useRef(null);
  const refetchRef = useRef(null);
  const [builderPlatforms, setBuilderPlatforms] = useState([]);
  const [selectedBuilderPlatform, setSelectedBuilderPlatform] = useState("");

  useEffect(() => {
    const fetchBuilderPlatforms = async () => {
      try {
        const response = await axios.get(`${apiUrl}/builder-platform?page[number]=1&page[size]=1000&sort=store_name`);
        const platforms = response.data.data || [];
        setBuilderPlatforms(platforms);
      } catch (error) {
        console.error("Failed to fetch builder platforms:", error);
        setBuilderPlatforms([]);
      }
    };

    fetchBuilderPlatforms();
  }, []);

  const handleInvalidDrop = useCallback(() => {
    notify("Cannot move materials between different option categories. Materials can only be reordered within the same option category.", { 
      type: "warning",
      autoHideDuration: 4000 
    });
  }, [notify]);

  if (!permissions) return <Loading />;

  const handleSave = async () => {
    if (!currentData || currentData.length === 0) return;

    setIsSaving(true);
    try {
      // Prepare payload matching the API structure
      const payload = {
        data: currentData.map((category) => ({
          option_category: category.option_category,
          sort_order: category.sort_order,
          materials: category.materials.map((material) => ({
            material_name: material.material_name,
            sort_order: material.sort_order,
          })),
        })),
      };

      // Add builder_platform_id to payload if a builder platform is selected
      if (selectedBuilderPlatform) {
        payload.builder_platform_id = parseInt(selectedBuilderPlatform);
      }

      // Make PUT request with all data
      await axios.put(`${apiUrl}/ring-material-sort-order`, payload);

      notify("Sort order updated successfully", { type: "success" });
      setHasChanges(false);
      // Refresh data without resetting builder platform selection
      if (refetchRef.current) {
        refetchRef.current();
      }
    } catch (error) {
      notify("Failed to update sort order", { type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    if (discardRef.current) {
      discardRef.current();
    }
    setHasChanges(false);
  };

  const handleBuilderPlatformChange = (value) => {
    setSelectedBuilderPlatform(value);
    setHasChanges(false);
    setCurrentData([]);
  };

  return (
    <List
      {...props}
      exporter={false}
      pagination={false}
      actions={
        <SortOrderListActions
          hasChanges={hasChanges}
          onSave={handleSave}
          onDiscard={handleDiscard}
          isSaving={isSaving}
          builderPlatforms={builderPlatforms}
          selectedBuilderPlatform={selectedBuilderPlatform}
          onBuilderPlatformChange={handleBuilderPlatformChange}
        />
      }
    >
      <Paper>
        <SortOrderTable
          onDataChange={setCurrentData}
          onHasChangesChange={setHasChanges}
          onDiscardRef={discardRef}
          onRefetchRef={refetchRef}
          builderPlatformId={selectedBuilderPlatform || null}
          onInvalidDrop={handleInvalidDrop}
        />
      </Paper>
    </List>
  );
};

export default SortOrderList;
